import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { checkAddPermission, checkEditPermission, checkDeletePermission, handleImportClickFile, handleUpdateAllCheckbox, handleUpdateDataCheckbox, handleExportFile } from "../../../Utils/helper";
import { getApi, postApi, putApi, deleteApi } from "../../../Services/apiService";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import Import from "../../../Utils/import";
import { TableFooter } from "../../../Utils/helper";
import DeleteModal from "../../../Utils/deleteModal";

const Buyer = () => {
    const navigate = useNavigate()
    const [methodType, setMethodType] = useState('');
    const { data } = useSelector((state) => state.roleData);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [buyers, setBuyers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [buyerToDelete, setBuyerToDelete] = useState(null);
    const [buyerToEdit, setBuyerToEdit] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importErrors, setImportErrors] = useState({
        file: '',
        general: ''
    });
    const [isImporting, setIsImporting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(1);

    const userData = JSON.parse(localStorage.getItem('userDetail'))
    console.log("userDatauserData", userData);
    // Form states
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        is_active: true  // Default to active when creating new buyer
    });
    const [errors, setErrors] = useState({
        email: '',
        name: ''
    });
    const [touched, setTouched] = useState({
        email: false,
        name: false
    });

    // Fetch buyers with pagination
    const fetchBuyers = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await getApi(`/buyers?search=${encodeURIComponent(searchQuery)}`);

            if (response?.success) {
                setBuyers(response.data.buyers || []);
                // setPageCount(response.data.buyers?.last_page || 1);
            } else {
                setError(response?.msg || 'Failed to fetch buyers');
            }
        } catch (error) {
            setError(error.message || 'Failed to fetch buyers');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchBuyers();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handlePageClick = (selected) => {
        setCurrentPage(selected);
    };

    // Toggle active status
    const toggleActiveStatus = async (buyer) => {
        try {
            setIsLoading(true);
            const response = await putApi(`/buyers/${buyer.id}`, {
                is_active: !buyer.is_active
            });

            if (response?.success) {
                toast.success(response.msg || 'Status updated successfully');
                if (buyer.id === buyer?.id) {
                    // localStorage.clear()
                    // navigate('/')
                }
                await fetchBuyers();
            } else {
                toast.error(response?.msg || 'Failed to update status');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.msg || error.message || 'Failed to update status';
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (touched[name]) {
            validateField(name, value);
        }
    };

    // Validate single field
    const validateField = (fieldName, value) => {
        let error = '';

        if (!value.trim() && fieldName !== 'is_active') {
            error = `${fieldName} is required`;
        } else if (fieldName === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = 'Please enter a valid email address';
        } else if (fieldName === 'name' && value.length > 50) {
            error = 'Name should be less than 50 characters';
        }

        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));

        return !error;
    };

    // Handle blur
    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
        validateField(name, value);
    };

    // Validate entire form
    const validateForm = () => {
        let isValid = true;
        const newTouched = {
            email: true,
            name: true
        };
        setTouched(newTouched);

        Object.keys(formData).forEach(key => {
            if (key !== 'is_active') {
                const valid = validateField(key, formData[key]);
                if (!valid) isValid = false;
            }
        });

        return isValid;
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            email: '',
            name: '',
            is_active: true
        });
        setErrors({
            email: '',
            name: ''
        });
        setTouched({
            email: false,
            name: false
        });
    };

    // Add new buyer
    const handleAddBuyer = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await postApi('/buyers', formData);

            if (response?.success) {
                toast.success(response.msg || 'Buyer added successfully');
                await fetchBuyers();
                setMethodType('');
                resetForm();
            } else {
                toast.error(response?.msg || 'Failed to add buyer');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.msg || error.message || 'Failed to add buyer';
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    // Set up edit form
    const setupEditForm = (buyer) => {
        setFormData({
            email: buyer.email,
            name: buyer.name,
            is_active: buyer.is_active
        });
        setMethodType('edit');
        setBuyerToEdit(buyer);
    };

    // Update buyer
    const handleUpdateBuyer = async () => {
        if (!validateForm() || !buyerToEdit) {
            // toast.error('Please fix the validation errors');
            return;
        }

        try {
            setIsLoading(true);
            const response = await putApi(`/buyers/${buyerToEdit.id}`, formData);

            if (response?.success) {
                toast.success(response.msg || 'Buyer updated successfully');
                await fetchBuyers();
                setMethodType('');
                resetForm();
            } else {
                toast.error(response?.msg || 'Failed to update buyer');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.msg || error.message || 'Failed to update buyer';
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className='RightSideDiv product-single buyer-page-ui'>
            {/* Delete Confirmation Modal */}
            {/* Import modal */}
            {showImportModal && <Import setImportFile={setImportFile} importFile={importFile} searchQuery={searchQuery} fetchData={fetchBuyers} setShowImportModal={setShowImportModal} routeEnd={'/buyers/import'} routeexport={'/buyers/export'} type={'Buyer-Member'} />
            }
            {showDeleteModal && (
                <DeleteModal
                    setShowDeleteModal={setShowDeleteModal}
                    todelete={buyerToDelete}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    settoDelete={setBuyerToDelete}
                    fetchData={fetchBuyers}
                    route={`/buyers/${buyerToDelete?.id}`}
                    type={'buyer'}
                />
            )}

            {/* Buyers List */}
            {methodType === '' && (
                <div className='col-sm-12'>
                    <div className='setting-right setting-right-table'>
                        <div className="table-header table-actions">
                            <div className="search-card ">
                                <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="Search" />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search Buyer Member..."
                                    value={searchQuery}
                                    onChange={(e) => {setSearchQuery(e.target.value);setCurrentPage(1)}}
                                />
                                  
                            </div>
                            {/* <div className="table-actions"> */}
                            {checkAddPermission(data?.permissions, 'buyer_member') && (
                                <button className="btn" onClick={() => handleImportClickFile(setShowImportModal, setImportFile, setImportErrors)}>
                                    <img src={window.getPublicUrl('/assets/images/import.svg')} alt="Import" />
                                    Import
                                </button>
                            )}
                                <button className="btn" onClick={() => handleExportFile(buyers, searchQuery, 'buyerExport.xlsx', '/buyers/export')}>
                                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="Export" />
                                    Export
                                </button>
                                {checkAddPermission(data?.permissions, 'buyer_member') && (
                                    <button className="btn add" onClick={() => setMethodType('add')}>
                                        + Add Buyer Member
                                    </button>
                                )}
                            {/* </div> */}
                        </div>



                        <div className='permission-table-inner category-table'>
                            <table className="custom-table table-striped">
                                <thead>
                                    <tr>
                                        <th className="checkbox">
                                            <label className="checbox-cs">
                                                <input type="checkbox" onClick={(e) => handleUpdateAllCheckbox(e?.target?.checked, setBuyers)} />
                                                <span className="checkmark"></span>
                                            </label>
                                        </th>
                                        <th className="code">Sr. No.</th>
                                        <th className="name">Name</th>
                                        <th className="email">Email</th>
                                        <th className="status">Status</th>
                                        <th className="action">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : error ? (
                                        <tr>
                                            <td colSpan="6" className="text-center text-danger">{error}</td>
                                        </tr>
                                    ) : buyers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">No buyers found</td>
                                        </tr>
                                    ) : (
                                        buyers.map((buyer, index) => (
                                            <tr key={buyer.id}>
                                                <td className="checkbox">
                                                    <label className="checbox-cs">
                                                        <input type="checkbox" onClick={(e) => handleUpdateDataCheckbox(buyer, e, setBuyers)} checked={buyer?.status} />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </td>
                                                <td className="code">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                <td className="name">{buyer.name}</td>
                                                <td className="email">{buyer.email}</td>
                                                <td className="status">
                                                    <span className={`status-badge ${buyer.is_active ? 'active' : 'inactive'}`}>
                                                        {buyer.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="action action-cs">
                                                    <div className="dropdown">
                                                     {(checkEditPermission(data?.permissions, 'buyer_member') || checkDeletePermission(data?.permissions, 'buyer_member')) && (
                                                        <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                                            Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                                                        </button>
                                                     )}
                                                        <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                                         <li>
                                                         {checkEditPermission(data?.permissions, 'buyer_member') && (
                                                                <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setupEditForm(buyer); }}>
                                                                    Edit
                                                                </a>
                                                            )}
                                                         </li>
                                                         <li>
                                                         {checkDeletePermission(data?.permissions, 'buyer_member') && (
                                                                <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); setBuyerToDelete(buyer); setShowDeleteModal(true); }}>
                                                                    Delete
                                                                </a>
                                                            )}
                                                         </li>
                                                         <li>
                                                         {checkEditPermission(data?.permissions, 'buyer_member') && (
                                                                <a href="#" class="dropdown-item" onClick={(e) => { e.preventDefault(); toggleActiveStatus(buyer); }}>
                                                                    {buyer.is_active ? 'Make Inactive' : 'Make Active'}
                                                                </a>
                                                            )}
                                                         </li>
                                                         </ul>
                                                            
                                                           
                                                           
                                                        
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                        
                        </div>
                        {/* <div className="table-footer d-flex justify-content-between table-pagination">
                                <select
                                    name=""
                                    id=""
                                    className="affter-change-cs page"
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1); // Reset to first page when changing items per page
                                    }}
                                >
                                    <option value="20">20</option>
                                    <option value="15">15</option>
                                    <option value="10">10</option>
                                    <option value="5">5</option>
                                </select>
                                <div>
                                    <TableFooter
                                        pageCount={pageCount}
                                        handlePageClick={handlePageClick}
                                        currentPage={currentPage}
                                    />
                                </div>
                            </div> */}
                    </div>
                </div>
            )}

            {/* Add Buyer Form */}
            {methodType === 'add' && (
                <div className='col-sm-12'>
                    <div className='setting-right position-relative'>
                        <h3>Add New Member</h3>
                        <img
                            src={window.getPublicUrl('/assets/images/close.svg')}
                            className='close'
                            alt="Close"
                            onClick={() => {
                                setMethodType('');
                                resetForm();
                            }}
                        />
                        <div className='setting-inner'>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <div className='form-group'>
                                        <label>Email Address<strong>*</strong></label>
                                        <input
                                            type="text"
                                            className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                            placeholder="Enter Email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                        />
                                        {touched.email && errors.email && (
                                            <div className="invalid-feedback d-block">
                                                {errors.email}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='col-sm-6'>
                                    <div className='form-group'>
                                        <label>Name<strong>*</strong></label>
                                        <input
                                            type="text"
                                            className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                            placeholder="Enter Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                        />
                                        {touched.name && errors.name && (
                                            <div className="invalid-feedback d-block">
                                                {errors.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className='button-settings'>
                            <div className='col-sm-12'>
                                <div className='d-flex justify-content-end gap-3'>
                                    <button className='reset' onClick={resetForm}>
                                        Reset
                                    </button>
                                    <button
                                        className='save'
                                        onClick={handleAddBuyer}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Buyer Form */}
            {methodType === 'edit' && (
                <div className='col-sm-12'>
                    <div className='setting-right position-relative'>
                        <h3>Edit Member</h3>
                        <img
                            src={window.getPublicUrl('/assets/images/close.svg')}
                            className='close'
                            alt="Close"
                            onClick={() => {
                                setMethodType('');
                                resetForm();
                            }}
                        />
                        <div className='setting-inner'>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <div className='form-group'>
                                        <label>Email Address<strong>*</strong></label>
                                        <input
                                            type="text"
                                            className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                                            placeholder="Enter Email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            disabled
                                        />
                                        {touched.email && errors.email && (
                                            <div className="invalid-feedback d-block">
                                                {errors.email}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='col-sm-6'>
                                    <div className='form-group'>
                                        <label>Full Name<strong>*</strong></label>
                                        <input
                                            type="text"
                                            className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                            placeholder="Enter Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                        />
                                        {touched.name && errors.name && (
                                            <div className="invalid-feedback d-block">
                                                {errors.name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className='button-settings'>
                            <div className='col-sm-12'>
                                <div className='d-flex justify-content-end gap-3'>
                                    <button
                                        className='reset'
                                        onClick={() => {
                                            setFormData({
                                                email: buyerToEdit?.email || '',
                                                name: buyerToEdit?.name || '',
                                                is_active: buyerToEdit?.is_active || true
                                            });
                                            setErrors({ email: '', name: '' });
                                        }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        className='save'
                                        onClick={handleUpdateBuyer}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Updating...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Buyer;